import React from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect } from '../../../shared/components';
import { Product } from '../../products/types/Product';
import { ServiceOffering } from '../../services/types/Service';
import { DraftLine, previewLineTotal } from '../invoiceForm';
import { InvoiceLineType } from '../types/Invoice';
import catalog from '../../catalog/Catalog.module.css';
import styles from '../Invoice.module.css';

type InvoiceLinesEditorProps = {
  lines: DraftLine[];
  pricesIncludeTax: boolean;
  products: Product[];
  services: ServiceOffering[];
  onChange: (lines: DraftLine[]) => void;
};

export const InvoiceLinesEditor: React.FC<InvoiceLinesEditorProps> = ({
  lines,
  pricesIncludeTax,
  products,
  services,
  onChange,
}) => {
  const { t } = useTranslation('invoices');

  const updateLine = (key: string, patch: Partial<DraftLine>) => {
    onChange(lines.map(line => line.key === key ? { ...line, ...patch } : line));
  };

  const applyCatalog = (line: DraftLine, lineType: InvoiceLineType, catalogId: string) => {
    if (lineType === 'product') {
      const product = products.find(item => item.id === catalogId);
      updateLine(line.key, {
        lineType,
        productId: catalogId,
        serviceOfferingId: '',
        name: product?.displayName || product?.name || line.name,
        unitPrice: product?.salePrice === undefined ? line.unitPrice : String(product.salePrice),
        taxPercent: product?.taxPercent === undefined ? line.taxPercent : String(product.taxPercent),
      });
      return;
    }

    if (lineType === 'service') {
      const service = services.find(item => item.id === catalogId);
      updateLine(line.key, {
        lineType,
        serviceOfferingId: catalogId,
        productId: '',
        name: service?.name || line.name,
        unitPrice: service?.salePrice === undefined ? line.unitPrice : String(service.salePrice),
        taxPercent: service?.taxPercent === undefined ? line.taxPercent : String(service.taxPercent),
      });
    }
  };

  return (
    <div className={styles.lineList}>
      {lines.map(line => (
        <div key={line.key} className={styles.lineCard}>
          <div className={catalog.field}>
            <label className={catalog.label}>{t('lineType')}</label>
            <select
              className={catalog.input}
              value={line.lineType}
              onChange={event => {
                const lineType = event.target.value as InvoiceLineType;
                updateLine(line.key, {
                  lineType,
                  productId: '',
                  serviceOfferingId: '',
                  name: lineType === 'custom' ? line.name : '',
                });
              }}
            >
              <option value="product">{t('lineProduct')}</option>
              <option value="service">{t('lineService')}</option>
              <option value="custom">{t('lineCustom')}</option>
            </select>
          </div>

          <div className={catalog.field}>
            <label className={catalog.label}>{t('lineName')}</label>
            {line.lineType === 'product' ? (
              <SearchableSelect
                value={line.productId}
                onChange={value => applyCatalog(line, 'product', value)}
                options={products.map(item => ({ value: item.id, label: item.displayName || item.name, meta: item.code }))}
                placeholder={t('selectProduct')}
                inputClassName={catalog.input}
              />
            ) : null}
            {line.lineType === 'service' ? (
              <SearchableSelect
                value={line.serviceOfferingId}
                onChange={value => applyCatalog(line, 'service', value)}
                options={services.map(item => ({ value: item.id, label: item.name, meta: item.code }))}
                placeholder={t('selectService')}
                inputClassName={catalog.input}
              />
            ) : null}
            {line.lineType === 'custom' ? (
              <input className={catalog.input} value={line.name} onChange={event => updateLine(line.key, { name: event.target.value })} />
            ) : null}
          </div>

          <button className="btn btn-secondary" type="button" onClick={() => onChange(lines.filter(item => item.key !== line.key))}>
            {t('removeLine')}
          </button>

          <div className={styles.lineMetrics}>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('quantity')}</label>
              <input className={catalog.input} value={line.quantity} onChange={event => updateLine(line.key, { quantity: event.target.value })} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('unitPrice')}</label>
              <input className={catalog.input} value={line.unitPrice} onChange={event => updateLine(line.key, { unitPrice: event.target.value })} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('discountPercent')}</label>
              <input className={catalog.input} value={line.discountPercent} onChange={event => updateLine(line.key, { discountPercent: event.target.value })} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('taxPercent')}</label>
              <input className={catalog.input} value={line.taxPercent} onChange={event => updateLine(line.key, { taxPercent: event.target.value })} />
            </div>
            <div className={catalog.field}>
              <label className={catalog.label}>{t('lineTotal')}</label>
              <div className={catalog.detailValue}>{previewLineTotal(line, pricesIncludeTax).toFixed(2)}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
